import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  GenericDropdownContext,
  GenericSelect,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import styled from "styled-components";
import { FilterTrigger } from "@/components/TableFilters";

const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

export type SelectOption<T extends string> = { value: T; label: string };

// Figma 13017-8763 renders form selects as a full-width secondary button with
// a trailing chevron — the same trigger the table filters use, upsized from
// button size S to M and stretched so the chevron sits at the far edge.
const ButtonTrigger = styled(FilterTrigger)`
  width: 100%;
  height: ${BUTTON.m.size}px;
  padding: 0 ${BUTTON.m.paddingw}px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};

  &:disabled {
    pointer-events: none;
    color: ${(props) => props.theme.buttonSecondaryDisabledText};
    background: ${(props) => props.theme.buttonSecondaryDisabledBackground};
    border-color: ${(props) => props.theme.buttonSecondaryDisabledBorder};
  }
`;

// flex: 1 pushes the chevron to the trigger's far edge; min-width lets long
// labels truncate instead of growing the trigger.
const TriggerLabel = styled.span<{ $placeholder?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  color: ${(props) =>
    props.$placeholder ? props.theme.semanticTextPlaceholder : "inherit"};
`;

// Design-system single-select: a trigger opening a GenericDropdown that
// matches the trigger's width, with a checkmark on the current selection.
// The default "button" trigger matches Figma's form selects; "select" keeps
// the underline GenericSelect look for modal forms built on GenericInput.
export function SelectDropdown<T extends string>({
  value,
  options,
  onChange,
  disabled,
  placeholder,
  trigger = "button",
  leadingIcon,
  floatingZIndex,
  onClear,
}: {
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  /** Makes the selection reversible: clicking the selected option again
   *  calls this instead of re-selecting it. Only for selects whose empty
   *  state is a real value (the service pickers), not typed unions. */
  onClear?: () => void;
  disabled?: boolean;
  /** Grayed text shown on the trigger while no option is selected. */
  placeholder?: string;
  trigger?: "button" | "select";
  /** Icon before the label on the "button" trigger (Figma Button
   *  leadingIcon, e.g. a Folder for a service picker). */
  leadingIcon?: Icon;
  /** Lifts the menu above a stacking context that would otherwise cover
   *  it — a modal's backdrop (see MODAL_Z_INDEX). */
  floatingZIndex?: number;
}) {
  const toItem = (option: SelectOption<T>): ListItemProps => ({
    id: option.value || option.label,
    type: "text",
    value: option.value,
    label: option.label,
    closeOnSelect: true,
    selected: { value: option.value === value, icon: "dot" },
    onClick: () =>
      option.value === value && onClear ? onClear() : onChange(option.value),
  });

  const selected = options.find((o) => o.value === value);
  const label = selected?.label ?? placeholder ?? "";

  return (
    <GenericDropdown
      dropdownItems={[options.map(toItem)]}
      width={{ strategy: "parent" }}
      disabled={disabled}
      floatingZIndex={floatingZIndex}
    >
      <GenericDropdownContext.Consumer>
        {({ open }) =>
          trigger === "button" ? (
            <ButtonTrigger type="button" $active={open} disabled={disabled}>
              {leadingIcon !== undefined && (
                <IconSVG
                  icon={leadingIcon}
                  size={BUTTON.iconsize}
                  svgColorKey={SVGColorKey.semanticTextBody}
                />
              )}
              <TriggerLabel $placeholder={!selected}>{label}</TriggerLabel>
              <IconSVG
                icon={Icon.BaseChevron}
                size={BUTTON.chevron}
                svgColorKey={SVGColorKey.semanticTextBody}
              />
            </ButtonTrigger>
          ) : (
            <GenericSelect
              // GenericSelect renders a <button> without a type attribute,
              // which defaults to "submit" inside a form — clicking the
              // trigger would fire native form validation ("Please fill out
              // this field") and, on a valid form, submit it.
              ref={(el) => el?.setAttribute("type", "button")}
              value={label}
              active={open}
              disabled={disabled}
            />
          )
        }
      </GenericDropdownContext.Consumer>
    </GenericDropdown>
  );
}
